// The board TOOLBAR (brief §5.2, prototype boardView() toolbar): the back-to-landing crumb, the honest
// sweep stamps (last sweep outcome + next due — straight off the projection's SweepStamp, G-182), the
// axis switcher (Board|List|Calendar|Timeline — a tab renders ONLY for an axis the projection's
// `availableViews` offers; an unavailable axis is ABSENT, never a disabled tease — brief §5.2), and the
// optional Run-now affordance. Run-now is a CALLBACK the container wires (the WorkApi carries no
// sweep-trigger method this run); when unwired the control does not render — no dead button.

import { StatusPill } from '../../../primitives.tsx';
import type { SweepStamp, WorkView } from '../../projections.ts';
import type { BoardAxis } from '../board-model.ts';
import { BackIcon, PlayIcon } from '../board-icons.tsx';

const AXES: ReadonlyArray<{
  readonly axis: BoardAxis;
  readonly view: WorkView;
  readonly label: string;
}> = [
  { axis: 'kanban', view: 'kanban', label: 'Board' },
  { axis: 'list', view: 'list', label: 'List' },
  { axis: 'calendar', view: 'calendar', label: 'Calendar' },
  { axis: 'timeline', view: 'timeline', label: 'Timeline' },
];

export interface BoardToolbarProps {
  readonly definitionName: string;
  readonly lastSweep: SweepStamp;
  readonly nextDueAt: string | null;
  readonly axis: BoardAxis;
  /** The axes this board offers — the switcher renders exactly this set, in the AXES order. */
  readonly availableViews: readonly WorkView[];
  readonly onAxisChange: (axis: BoardAxis) => void;
  readonly onBack: () => void;
  readonly onRunNow: (() => void) | undefined;
}

function SweepStamps({
  lastSweep,
  nextDueAt,
}: Pick<BoardToolbarProps, 'lastSweep' | 'nextDueAt'>): React.JSX.Element {
  return (
    <>
      {lastSweep !== null ? (
        <StatusPill tone={lastSweep.outcome === 'completed' ? 'go' : 'attention'}>
          Swept {lastSweep.at}
        </StatusPill>
      ) : (
        <StatusPill tone="neutral">No sweep yet</StatusPill>
      )}
      {nextDueAt !== null ? (
        <span className="ritona-numeric text-xs text-text-subtle">next {nextDueAt}</span>
      ) : null}
    </>
  );
}

function AxisSwitch({
  axis,
  availableViews,
  onAxisChange,
}: Pick<BoardToolbarProps, 'axis' | 'availableViews' | 'onAxisChange'>): React.JSX.Element {
  const offered = new Set<WorkView>(availableViews);
  return (
    <div
      role="group"
      aria-label="Board axis"
      className="inline-flex rounded-sm bg-surface-sunken p-0.5"
    >
      {AXES.filter(({ view }) => offered.has(view)).map(({ axis: candidate, label }) => (
        <button
          key={candidate}
          type="button"
          aria-pressed={candidate === axis}
          onClick={() => onAxisChange(candidate)}
          className={
            'rounded-sm px-3 py-1 text-sm font-medium transition-colors ' +
            (candidate === axis ? 'bg-surface text-text' : 'text-text-muted hover:text-text')
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** The crumb + stamps + axis switcher + run-now row above the board. */
export function BoardToolbar({
  definitionName,
  lastSweep,
  nextDueAt,
  axis,
  availableViews,
  onAxisChange,
  onBack,
  onRunNow,
}: BoardToolbarProps): React.JSX.Element {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to workflows"
          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-1 text-text-muted hover:text-text"
        >
          <BackIcon size={14} />
          Work
        </button>
        <span className="text-text-subtle">/</span>
        <span className="font-medium text-text">{definitionName}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <SweepStamps lastSweep={lastSweep} nextDueAt={nextDueAt} />
        <AxisSwitch axis={axis} availableViews={availableViews} onAxisChange={onAxisChange} />
        {onRunNow !== undefined ? (
          <button
            type="button"
            onClick={onRunNow}
            className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:border-border-strong"
          >
            <PlayIcon size={13} />
            Run now
          </button>
        ) : null}
      </div>
    </div>
  );
}
